import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Waveform, CheckCircle, WhatsappLogo, UploadSimple, Sparkle } from "@phosphor-icons/react";

export default function StemsBuyCredits() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(0);
  const [selected, setSelected] = useState("five");
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/stems/balance").then(({ data }) => {
      setPacks(data.packs || []);
      setBalance(data.credits || 0);
    });
  }, [user, nav]);

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stems/buy-credits", { pack: selected });
      setTx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando compra");
    } finally { setLoading(false); }
  };

  const uploadReceipt = async (file) => {
    if (!tx) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máximo 8MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("transaction_id", tx.transaction_id);
      await api.post("/payments/bbva/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("¡Comprobante enviado! Validaremos en horas.");
      setTx(t => ({ ...t, status: "pending_review", has_receipt: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo comprobante");
    } finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/library" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5" data-testid="stems-buy-back">
          <ArrowLeft size={16} weight="bold" /> Volver a mi música
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <Waveform weight="fill" size={22} className="text-[#7FD7FF]" />
          <p className="text-sm uppercase tracking-widest text-[#7FD7FF]">Separación de stems</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-4">Aísla cada instrumento</h1>
        <p className="text-white/65 mb-8 leading-relaxed">
          Compra créditos para separar tus canciones en <span className="text-white">voces, batería, bajo y melodía</span>.
          La IA aísla cada pista en 1-2 minutos. Descarga cada una para mezclar en tu DAW.
        </p>

        <div className="rounded-2xl bg-[#121212] border border-white/10 p-4 mb-8 flex items-center gap-3" data-testid="stems-buy-balance">
          <Sparkle weight="fill" size={18} className="text-[#7FD7FF]" />
          <p className="text-sm">Créditos disponibles ahora: <span className="text-[#7FD7FF] font-display font-black text-xl ml-2" data-testid="stems-buy-current">{balance}</span></p>
        </div>

        {!tx ? (
          <>
            <p className="text-xs uppercase tracking-widest text-white/60 mb-3">Elige tu paquete</p>
            <div className="space-y-3 mb-6">
              {packs.map(p => {
                const on = selected === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setSelected(p.key)}
                    className={`w-full text-left rounded-2xl p-5 border transition-all active:scale-[0.99] ${on ? "border-[#7FD7FF] bg-[#7FD7FF]/5" : "border-white/10 bg-[#121212] hover:border-white/30"} ${p.popular ? "ring-1 ring-[#FFB800]/30" : ""}`}
                    data-testid={`stem-pack-${p.key}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-display font-bold text-lg">{p.name}</p>
                          {p.popular && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFB800]/15 text-[#FFB800]">Más popular</span>}
                          {on && <CheckCircle weight="fill" size={18} className="text-[#7FD7FF] ml-auto" />}
                        </div>
                        <p className="text-xs text-white/50">{p.tagline}</p>
                      </div>
                      <p className="font-display font-black text-2xl text-[#7FD7FF] shrink-0">${p.price_mxn}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <button onClick={buy} disabled={loading} className="w-full py-4 rounded-full bg-[#7FD7FF] hover:bg-[#5FC0EE] disabled:opacity-40 text-black font-bold active:scale-95 transition-all" data-testid="stems-buy-confirm">
              {loading ? "Cargando…" : "Continuar con el pago"}
            </button>
          </>
        ) : (
          <div className="rounded-2xl bg-[#121212] border border-white/10 p-6 space-y-4" data-testid="stems-buy-tx">
            <p className="text-sm text-white/60">Estás comprando: <span className="text-white font-semibold">{tx.pack.name}</span> — <span className="text-[#7FD7FF] font-bold">${tx.amount} MXN</span></p>
            <div className="rounded-xl bg-black/40 p-5 border border-white/10 space-y-2 text-sm">
              <Row label="Banco" value={tx.bank} />
              <Row label="Beneficiario" value={tx.account_name} />
              <Row label="CLABE" value={tx.clabe_or_account} mono />
              <Row label="Monto" value={`$${tx.amount} MXN`} accent="#B4F34A" />
              <Row label="Referencia" value={tx.reference} mono accent="#FFB800" />
            </div>
            <a href={tx.whatsapp_link} target="_blank" rel="noreferrer" className="w-full py-3 rounded-full bg-[#25D366] hover:bg-[#1FB858] text-black font-semibold flex items-center justify-center gap-2" data-testid="stems-buy-whatsapp">
              <WhatsappLogo weight="fill" size={20} /> Reportar pago por WhatsApp
            </a>
            <div className="rounded-xl bg-white/5 p-4 border border-white/10">
              <p className="text-sm font-semibold mb-2">Sube tu comprobante</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadReceipt(e.target.files[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="stems-buy-upload">
                <UploadSimple size={18} weight="bold" />
                {uploading ? "Subiendo…" : tx.has_receipt ? "Subir otro" : "Subir comprobante (foto)"}
              </button>
              {tx.has_receipt && <p className="text-xs text-[#B4F34A] mt-3">✓ Comprobante recibido. Validamos tu pago.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-white/50">{label}</span>
      <span className={mono ? "font-mono" : "font-semibold"} style={{ color: accent || "#fff" }}>{value}</span>
    </div>
  );
}
